import { Validation } from './validation.model';
import {
	UntypedFormArray,
	UntypedFormGroup,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import {
	OPERATOR_TYPE_STRING,
	OPERATOR_TYPE_DATES,
	OPERATOR_TYPE_NUMBER,
	CustomReportOperator,
} from './custom-report-operator.model';
import { DateTimeUtility } from '../utility/date-time.utility';
import { DateRangeValidator } from '../validators/date-range.validator';
import { CustomReportTable } from './custom-report-table.model';

export interface CustomReportCriteriaJSON {
	id: number;
	customReportId: number;
	criteriaCategory: string;
	whereField: string;
	conditionDataType: string;
	conditionValue1: string;
	conditionValue2: string;
	whereOperator: string;
	sequence: number;
}

export class CustomReportCriteria {
	constructor(
		public id: number,
		public customReportId: number,
		public criteriaCategory: string,
		public whereField: string,
		public conditionDataType: string,
		public conditionValue1: string,
		public conditionValue2: string,
		public whereOperator: string,
		public sequence: number
	) {}

	static fromJSON(json: CustomReportCriteriaJSON): CustomReportCriteria {
		return Object.assign(
			Object.create(CustomReportCriteria.prototype),
			json,
			{}
		);
	}

	static createFormArray(
		criteria: CustomReportCriteria[],
		tables: CustomReportTable[],
		dateOperators: CustomReportOperator[],
		stringOperators: CustomReportOperator[],
		validation: Validation
	): UntypedFormArray {
		return criteria
			? new UntypedFormArray(
					criteria.map((c) =>
						this.createFormGroup(
							c,
							tables,
							dateOperators,
							stringOperators,
							validation
						)
					)
				)
			: new UntypedFormArray([]);
	}

	static createFormGroup(
		c: CustomReportCriteria,
		tables: CustomReportTable[],
		dateOperators: CustomReportOperator[],
		stringOperators: CustomReportOperator[],
		validation: Validation
	): UntypedFormGroup {
		const fg: UntypedFormGroup = new UntypedFormGroup({
			id: new UntypedFormControl(c ? c.id : null),
			customReportId: new UntypedFormControl(c ? c.customReportId : null),
			criteriaCategory: new UntypedFormControl(
				c ? c.criteriaCategory : null,
				[Validators.required]
			),
			whereField: new UntypedFormControl(null, [Validators.required]),
			whereOperator: new UntypedFormControl(null, [Validators.required]),
			fields: new UntypedFormControl(),
			operators: new UntypedFormControl(),
			operatorType: new UntypedFormControl(),
			stringValue: new UntypedFormControl(null),
			dateValueAfter: new UntypedFormControl(null),
			dateValueBefore: new UntypedFormControl(null),
			numberValue: new UntypedFormControl(null),
		});
		this.hideStringInput(fg);
		this.hideDateRangeInput(fg);
		this.hideNumberInput(fg);
		fg.get('operatorType')
			.valueChanges.pipe(distinctUntilChanged())
			.subscribe((type) =>
				this.setFieldVisibility(fg, type, c, validation)
			);
		fg.get('whereOperator')
			.valueChanges.pipe(
				startWith(fg.get('whereOperator').value),
				distinctUntilChanged()
			)
			.subscribe((value) =>
				fg
					.get('operatorType')
					.setValue(
						CustomReportOperator.getOperatorType(
							value,
							fg.get('operators').value
						)
					)
			);
		fg.get('whereField')
			.valueChanges.pipe(
				startWith(fg.get('whereField').value),
				distinctUntilChanged()
			)
			.subscribe((value) => {
				fg.get('operators').setValue(
					CustomReportOperator.getOperatorsForWhereField(
						value,
						fg.get('fields').value,
						dateOperators,
						stringOperators
					)
				);
				fg.get('whereOperator').setValue(
					c && c.whereField === value ? c.whereOperator : null
				);
			});
		fg.get('criteriaCategory')
			.valueChanges.pipe(
				startWith(fg.get('criteriaCategory').value),
				distinctUntilChanged()
			)
			.subscribe((value) => {
				fg.get('fields').setValue(
					CustomReportTable.getFieldsForTable(value, tables)
				);
				fg.get('whereField').setValue(
					c && c.criteriaCategory === value ? c.whereField : null
				);
			});
		return fg;
	}

	private static setFieldVisibility(
		fg: UntypedFormGroup,
		type: string,
		c: CustomReportCriteria,
		validation: Validation
	): void {
		switch (type) {
			case OPERATOR_TYPE_STRING:
				this.showStringInput(fg, c, validation);
				break;
			case OPERATOR_TYPE_DATES:
				this.showDateRangeInput(fg, c);
				break;
			case OPERATOR_TYPE_NUMBER:
				this.showNumberInput(fg, c, validation);
				break;
		}
	}

	private static showStringInput(
		fg: UntypedFormGroup,
		c: CustomReportCriteria,
		validation: Validation
	): void {
		this.hideDateRangeInput(fg);
		this.hideNumberInput(fg);
		fg.get('stringValue').enable();
		fg.get('stringValue').setValue(c ? c.conditionValue1 : null);
		fg.get('stringValue').setValidators([
			Validators.required,
			Validators.maxLength(validation.comment.maxLength),
			Validators.pattern(validation.comment.pattern),
		]);
		fg.setValidators([]);
		fg.updateValueAndValidity();
	}

	private static hideStringInput(fg: UntypedFormGroup): void {
		fg.get('stringValue').disable();
		fg.get('stringValue').clearValidators();
	}

	private static showDateRangeInput(
		fg: UntypedFormGroup,
		c: CustomReportCriteria
	): void {
		this.hideStringInput(fg);
		this.hideNumberInput(fg);
		fg.get('dateValueAfter').enable();
		fg.get('dateValueAfter').setValue(
			c ? DateTimeUtility.fromDatabaseDate(c.conditionValue1) : null
		);
		fg.get('dateValueAfter').setValidators([Validators.required]);
		fg.get('dateValueBefore').enable();
		fg.get('dateValueBefore').setValue(
			c ? DateTimeUtility.fromDatabaseDate(c.conditionValue2) : null
		);
		fg.get('dateValueBefore').setValidators([Validators.required]);
		fg.setValidators([
			DateRangeValidator(
				fg.get('dateValueAfter'),
				fg.get('dateValueBefore')
			),
		]);
		fg.updateValueAndValidity();
	}

	private static hideDateRangeInput(fg: UntypedFormGroup): void {
		fg.get('dateValueAfter').disable();
		fg.get('dateValueAfter').clearValidators();
		fg.get('dateValueBefore').disable();
		fg.get('dateValueBefore').clearValidators();
	}

	private static showNumberInput(
		fg: UntypedFormGroup,
		c: CustomReportCriteria,
		validation: Validation
	): void {
		this.hideStringInput(fg);
		this.hideDateRangeInput(fg);
		fg.get('numberValue').enable();
		fg.get('numberValue').setValue(
			c && Number.isInteger(Number(c.conditionValue1))
				? c.conditionValue1
				: null
		);
		fg.get('numberValue').setValidators([
			Validators.required,
			Validators.maxLength(validation.number.maxLength),
			Validators.pattern(validation.number.pattern),
		]);
		fg.setValidators([]);
		fg.updateValueAndValidity();
	}

	private static hideNumberInput(fg: UntypedFormGroup): void {
		fg.get('numberValue').disable();
		fg.get('numberValue').clearValidators();
	}

	static fromFormArray(fa: UntypedFormArray): CustomReportCriteria[] {
		return fa.length > 0
			? fa.controls.map((fg: UntypedFormGroup) => this.fromFormGroup(fg))
			: [];
	}

	static fromFormGroup(fg: UntypedFormGroup): CustomReportCriteria {
		return Object.assign(Object.create(CustomReportCriteria.prototype), {
			id: fg.get('id').value,
			criteriaCategory: fg.get('criteriaCategory').value,
			whereField: fg.get('whereField').value,
			whereOperator: fg.get('whereOperator').value,
			conditionValue1: this.getConditionValue1(fg),
			conditionValue2: this.getConditionValue2(fg),
		});
	}

	private static getConditionValue1(fg: UntypedFormGroup): string {
		switch (fg.get('operatorType').value) {
			case OPERATOR_TYPE_STRING:
				return fg.get('stringValue').value;
			case OPERATOR_TYPE_DATES:
				return DateTimeUtility.toDatabaseDateString(
					fg.get('dateValueAfter').value
				);
			case OPERATOR_TYPE_NUMBER:
				return fg.get('numberValue').value;
		}
	}

	private static getConditionValue2(fg: UntypedFormGroup): string {
		return fg.get('operatorType').value === OPERATOR_TYPE_DATES
			? DateTimeUtility.toDatabaseDateString(
					fg.get('dateValueBefore').value
				)
			: '';
	}
}
